import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Calendar, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';
import CancelSubscriptionModal from './CancelSubscriptionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BillingTab = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch subscription and payment method in parallel
      const [subResponse, pmResponse] = await Promise.all([
        axios.get(`${API}/user/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/payment-method`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSubscription(subResponse.data);
      setPaymentMethod(pmResponse.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Fehler beim Laden der Abrechnungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleManagePaymentMethod = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/stripe/billing-portal`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Billing Portal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      
      // Show user-friendly error message from backend
      const errorMessage = error.response?.data?.detail || 'Fehler beim Öffnen des Billing Portals';
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-700',
      canceling: 'bg-yellow-100 text-yellow-700',
      past_due: 'bg-red-100 text-red-700',
      canceled: 'bg-gray-100 text-gray-700',
      inactive: 'bg-gray-100 text-gray-700'
    };

    const labels = {
      active: 'Aktiv',
      canceling: 'Wird gekündigt',
      past_due: 'Zahlung ausstehend',
      canceled: 'Gekündigt',
      inactive: 'Inaktiv'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status] || badges.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCardBrand = (brand) => {
    const brands = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover'
    };
    return brands[brand] || brand;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Zahlungen & Abonnement</h3>
        <p className="text-sm text-gray-500">Verwalten Sie Ihre Zahlungsmethode und Ihr Abo</p>
      </div>

      {/* Payment Method Section */}
      <div className="border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold">Zahlungsmethode</h4>
        </div>

        {paymentMethod?.has_payment_method ? (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
                {paymentMethod.type.toUpperCase()}
              </div>
              <div>
                <p className="font-medium">
                  {getCardBrand(paymentMethod.type)} •••• {paymentMethod.last4}
                </p>
                <p className="text-sm text-gray-500">
                  Gültig bis {paymentMethod.exp_month}/{paymentMethod.exp_year}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleManagePaymentMethod}
              size="sm"
              className="w-full md:w-auto"
            >
              Ändern
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 mb-3">Keine Zahlungsmethode hinterlegt</p>
            <Button
              variant="outline"
              onClick={handleManagePaymentMethod}
              size="sm"
              className="w-full md:w-auto"
            >
              Zahlungsmethode hinzufügen
            </Button>
          </div>
        )}
      </div>

      {/* Subscription Section */}
      <div className="border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold">Aktuelles Abonnement</h4>
        </div>

        {subscription?.has_subscription ? (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="font-medium">{subscription.plan || 'Standard Plan'}</p>
                <p className="text-2xl font-bold text-gray-900">
                  €{subscription.price?.toFixed(2) || '0.00'}
                  <span className="text-sm font-normal text-gray-500">
                    /{subscription.interval === 'month' ? 'Monat' : 'Jahr'}
                  </span>
                </p>
              </div>
              <div className="self-start md:self-auto">
                {getStatusBadge(subscription.status)}
              </div>
            </div>

            {subscription.next_billing_date && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Nächste Zahlung: {new Date(subscription.next_billing_date).toLocaleDateString('de-AT')}
                </span>
              </div>
            )}

            {subscription.cancel_at_period_end && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-900">Abo wird gekündigt</p>
                  <p className="text-sm text-yellow-700">
                    Ihr Zugang bleibt aktiv bis {new Date(subscription.next_billing_date).toLocaleDateString('de-AT')}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 mb-3">Kein aktives Abonnement</p>
            <p className="text-sm text-gray-400">
              Sie nutzen derzeit Einmalzahlungen oder haben kein aktives Abo
            </p>
          </div>
        )}
      </div>

      {/* Cancellation Section */}
      {subscription?.has_subscription && !subscription.cancel_at_period_end && (
        <div className="border border-red-200 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-red-900">Abonnement kündigen</h4>
          <p className="text-sm text-gray-600">
            Du kannst dein Abonnement jederzeit kündigen. Dein Zugang bleibt bis zum Ende der aktuellen Abrechnungsperiode aktiv.
          </p>
          <Button
            variant="destructive"
            onClick={() => setShowCancelModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white w-full md:w-auto"
          >
            Abo kündigen
          </Button>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSuccess={fetchBillingData}
      />
    </div>
  );
};

export default BillingTab;
